import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
} from "@angular/core";
import { Subject } from "rxjs";
import { ColumnConfig, TableAction, TableConfig } from "./table-setting";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";

@Component({
  selector: "app-table",
  templateUrl: "./table.component.html",
  styleUrls: ["./table.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent<T> implements OnInit, OnChanges, OnDestroy {
  // Data inputs
  @Input() data: T[] = [];
  @Input() config: TableConfig = new TableConfig();

  // Tree Data View Model
  displayData: any[] = [];

  getRowLevel(row: any): number {
    return row._level || 0;
  }

  hasChildren(row: any): boolean {
    return !!row._hasChildren;
  }

  // Pagination inputs
  @Input() totalItems: number = 0;
  @Input() currentPage: number = 1;
  @Input() pageSize: number = 10;

  // Display options
  @Input() loading: boolean = false;
  @Input() containerClass: string = "";

  // Template inputs
  @ContentChild("actionsTemplate") actionsTemplate!: TemplateRef<any>;
  @ContentChild("summaryTemplate") summaryTemplate!: TemplateRef<any>;

  // Column templates (dynamic)
  @Input() columnTemplates: { [key: string]: TemplateRef<any> } = {};

  // Outputs
  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() sortChange = new EventEmitter<{
    field: string;
    direction: 1 | -1;
  }>();
  @Output() rowClick = new EventEmitter<T>();
  @Output() actionClick = new EventEmitter<{ action: string; row: T }>();
  @Output() selectionChange = new EventEmitter<T[]>();

  // Internal state
  allChecked = false;
  indeterminate = false;
  selectedItems: T[] = [];
  goToPageNumber: number | null = null;

  // Column visibility state
  showColumnSettings = false;
  tempColumnVisibility: { [key: string]: boolean } = {};
  private originalColumnVisibility: { [key: string]: boolean } = {};

  private destroy$ = new Subject<void>();
  private originalColumnOrder: string[] = [];

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.initDefaults();
    this.loadColumnOrder(); // Load saved order
    this.loadColumnVisibility(); // Load saved visibility
  }

  // ...

  // ==================== DRAG & DROP & STORAGE ====================

  drop(event: CdkDragDrop<any>) {
    if (event.previousIndex === event.currentIndex) return;

    const columnMoved = event.item.data as ColumnConfig;
    if (!columnMoved) return;

    const visibleColumns = this.getVisibleColumns();

    // In Angular CDK, currentIndex is relative to draggable items only.
    // No need to subtract offset for static columns (Select/Numbering).
    const targetIndex = Math.max(
      0,
      Math.min(event.currentIndex, visibleColumns.length - 1)
    );
    const targetColumn = visibleColumns[targetIndex];

    if (!targetColumn || targetColumn.fieldName === columnMoved.fieldName)
      return;

    const masterPrevIndex = this.config.columns.findIndex(
      (c) => c.fieldName === columnMoved.fieldName
    );
    const masterTargetIndex = this.config.columns.findIndex(
      (c) => c.fieldName === targetColumn.fieldName
    );

    if (masterPrevIndex !== -1 && masterTargetIndex !== -1) {
      moveItemInArray(this.config.columns, masterPrevIndex, masterTargetIndex);
      this.config.columns = [...this.config.columns];
      this.saveColumnOrder();
      this.cdr.detectChanges();
    }
  }

  saveColumnOrder() {
    if (!this.config?.id) return;
    const order = this.config.columns.map((c) => c.fieldName);
    localStorage.setItem(
      `table_order_${this.config.id}`,
      JSON.stringify(order)
    );
  }

  loadColumnOrder() {
    if (!this.config?.id) return;
    const savedOrder = localStorage.getItem(`table_order_${this.config.id}`);
    if (savedOrder) {
      try {
        const order: string[] = JSON.parse(savedOrder);
        if (Array.isArray(order)) {
          this.config.columns.sort((a, b) => {
            const indexA = order.indexOf(a.fieldName);
            const indexB = order.indexOf(b.fieldName);

            if (indexA === -1 && indexB === -1) return 0;
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;

            return indexA - indexB;
          });
          // Trigger reference update
          this.config.columns = [...this.config.columns];
          this.cdr.markForCheck();
        }
      } catch (e) {
        console.error("Error loading table column order", e);
      }
    }
  }

  resetColumnOrder() {
    if (!this.config?.id || this.originalColumnOrder.length === 0) return;

    localStorage.removeItem(`table_order_${this.config.id}`);

    this.config.columns.sort((a, b) => {
      const indexA = this.originalColumnOrder.indexOf(a.fieldName);
      const indexB = this.originalColumnOrder.indexOf(b.fieldName);

      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });

    this.config.columns = [...this.config.columns];
    this.cdr.detectChanges();
  }

  // ==================== COLUMN VISIBILITY ====================

  loadColumnVisibility(): void {
    if (!this.config?.id) return;

    // Store original visibility state for reset
    this.config.columns.forEach((col) => {
      const isVisible = col.show !== false && col.isShow !== false;
      this.originalColumnVisibility[col.fieldName] = isVisible;
    });

    // Load saved visibility from localStorage
    const savedVisibility = localStorage.getItem(
      `table_visibility_${this.config.id}`
    );
    if (savedVisibility) {
      try {
        const visibility: { [key: string]: boolean } =
          JSON.parse(savedVisibility);
        this.config.columns.forEach((col) => {
          if (visibility.hasOwnProperty(col.fieldName)) {
            col.show = visibility[col.fieldName];
          }
        });
        this.cdr.markForCheck();
      } catch (e) {
        console.error("Error loading column visibility", e);
      }
    }
  }

  saveColumnVisibility(): void {
    if (!this.config?.id) return;
    const visibility: { [key: string]: boolean } = {};
    this.config.columns.forEach((col) => {
      visibility[col.fieldName] = col.show !== false;
    });
    localStorage.setItem(
      `table_visibility_${this.config.id}`,
      JSON.stringify(visibility)
    );
  }

  toggleColumnSettings(): void {
    this.showColumnSettings = !this.showColumnSettings;
    if (this.showColumnSettings) {
      // Initialize temp state when opening
      this.config.columns.forEach((col) => {
        this.tempColumnVisibility[col.fieldName] = col.show !== false;
      });
    }
  }

  onTempVisibilityChange(fieldName: string, checked: boolean): void {
    this.tempColumnVisibility[fieldName] = checked;
  }

  applyColumnVisibility(): void {
    // Apply temp visibility to columns
    this.config.columns.forEach((col) => {
      if (this.tempColumnVisibility.hasOwnProperty(col.fieldName)) {
        col.show = this.tempColumnVisibility[col.fieldName];
      }
    });
    this.saveColumnVisibility();
    this.showColumnSettings = false;
    this.cdr.detectChanges();
  }

  resetColumnVisibility(): void {
    // Reset to original visibility
    this.config.columns.forEach((col) => {
      if (this.originalColumnVisibility.hasOwnProperty(col.fieldName)) {
        col.show = this.originalColumnVisibility[col.fieldName];
        this.tempColumnVisibility[col.fieldName] = col.show !== false;
      } else {
        col.show = true;
        this.tempColumnVisibility[col.fieldName] = true;
      }
    });
    if (this.config?.id) {
      localStorage.removeItem(`table_visibility_${this.config.id}`);
    }
    this.showColumnSettings = false;
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["config"] && this.config) {
      this.initDefaults();
      this.loadColumnOrder();
    }
    if (changes["columnTemplates"]) {
      this.cdr.markForCheck();
    }
    if (changes["data"]) {
      this.updateSelectionState();
      this.initDisplayData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initDefaults(): void {
    const defaultConfig = new TableConfig();

    // Ensure config exists
    if (!this.config) {
      this.config = defaultConfig;
    }

    // Capture original order only once
    if (this.config.columns && this.originalColumnOrder.length === 0) {
      this.originalColumnOrder = this.config.columns.map((c) => c.fieldName);
    }

    // Set default display config
    this.config.display = {
      ...defaultConfig.display,
      ...this.config.display,
    };

    // Set default pagination config
    this.config.pagination = {
      ...defaultConfig.pagination,
      ...this.config.pagination,
    };
  }

  // ==================== SELECTION ====================

  onSelectAll(checked: boolean): void {
    if (!this.displayData) return;

    // Use displayData for flat selection or handle recursive selection?
    // Usually select all means select visible or select all loaded.
    // Let's stick to simple behavior: select all available in data (if using tree mode, this might be tricky if not flattened).
    // If tree mode, we interact with 'displayData' or 'data'.
    // 'data' is the root list. 'displayData' is potentially visible list.

    // For simplicity, iterate 'displayData' or 'data' recursively?
    // Let's assume selecting visible rows for now as standard behavior.

    const itemsToSelect = this.config.display?.treeMode
      ? this.getAllItemsRecursive(this.data)
      : this.data;

    itemsToSelect.forEach((item: any) => {
      if (!item._disabled) {
        item._selected = checked;
      }
    });

    this.updateSelectionState();
  }

  getAllItemsRecursive(nodes: any[]): any[] {
    let items: any[] = [];
    if (!nodes) return [];
    for (const node of nodes) {
      items.push(node);
      const children = node[this.config.display!.treeChildrenField!];
      if (children && children.length) {
        items = items.concat(this.getAllItemsRecursive(children));
      }
    }
    return items;
  }

  onSelectItem(item: any, checked: boolean): void {
    item._selected = checked;
    this.updateSelectionState();
  }

  private updateSelectionState(): void {
    // If tree mode, we need to gather all selected items recursively
    const allItems = this.config.display?.treeMode
      ? this.getAllItemsRecursive(this.data)
      : this.data;

    if (!allItems || allItems.length === 0) {
      this.allChecked = false;
      this.indeterminate = false;
      this.selectedItems = [];
      return;
    }

    const validItems = allItems.filter((item: any) => !item._disabled);
    const selectedCount = validItems.filter(
      (item: any) => item._selected
    ).length;

    this.allChecked =
      validItems.length > 0 && selectedCount === validItems.length;
    this.indeterminate = selectedCount > 0 && selectedCount < validItems.length;
    this.selectedItems = validItems.filter((item: any) => item._selected);

    this.selectionChange.emit(this.selectedItems);
    this.cdr.detectChanges();
  }

  // ==================== TREE LOGIC ====================

  initDisplayData(): void {
    if (
      this.config.display?.treeMode &&
      this.config.display.treeChildrenField
    ) {
      this.displayData = [];
      this.flattenTreeData(this.data, 0, []);
    } else {
      this.displayData = this.data;
    }
    this.cdr.markForCheck();
  }

  flattenTreeData(nodes: any[], level: number, parents: any[]): void {
    if (!nodes) return;
    for (const node of nodes) {
      if (!node._level) node._level = level;
      // Initialize expand state if undefined. Default collapsed (false) or keep existing.
      if (node._expanded === undefined) node._expanded = false;

      this.displayData.push(node);

      const children = node[this.config.display!.treeChildrenField!];
      if (children && children.length > 0) {
        node._hasChildren = true;
        // Only process children if expanded
        if (node._expanded) {
          this.flattenTreeData(children, level + 1, [...parents, node]);
        }
      } else {
        node._hasChildren = false;
      }
    }
  }

  onToggleExpand(row: any, event: Event): void {
    event.stopPropagation();
    row._expanded = !row._expanded;
    // Re-calculate display data on toggle
    this.initDisplayData();
  }

  // ==================== PAGINATION ====================

  onPageChange(page: number): void {
    this.currentPage = page;
    this.pageChange.emit(page);
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.pageSizeChange.emit(size);
  }

  getTotalPages(): number {
    return Math.ceil(this.totalItems / this.pageSize);
  }

  goToPage(): void {
    if (!this.goToPageNumber) return;

    const totalPages = this.getTotalPages();
    let targetPage = Math.floor(this.goToPageNumber);

    // Validate page number
    if (targetPage < 1) {
      targetPage = 1;
    } else if (targetPage > totalPages) {
      targetPage = totalPages;
    }

    if (targetPage !== this.currentPage) {
      this.currentPage = targetPage;
      this.pageChange.emit(targetPage);
    }

    // Reset input after navigation
    this.goToPageNumber = null;
  }

  // ==================== SORTING ====================

  onSort(column: ColumnConfig): void {
    if (!column.sortable) return;

    // Toggle sort direction
    const newDirection = column.sortDirection === 1 ? -1 : 1;

    // Reset all other columns
    this.config.columns.forEach((col) => {
      if (col.fieldName !== column.fieldName) {
        col.sortDirection = null;
      }
    });

    column.sortDirection = newDirection;

    this.sortChange.emit({
      field: column.sortField || column.fieldName,
      direction: newDirection,
    });
  }

  // ==================== ACTIONS ====================

  onActionClick(action: TableAction, row: T): void {
    this.actionClick.emit({ action: action.type, row });
  }

  onRowClick(row: T): void {
    this.rowClick.emit(row);
  }

  // ==================== DATA DISPLAY ====================

  getVisibleColumns(): ColumnConfig[] {
    return (
      this.config?.columns?.filter((col) => {
        // Support both 'show' and 'isShow' (backward compatibility)
        const isVisible = col.show !== false && col.isShow !== false;
        if (!isVisible) return false;
        if (col.showIf && !col.showIf()) return false;
        return true;
      }) || []
    );
  }

  getColumnValue(row: any, column: ColumnConfig): any {
    if (column.display) {
      return column.display(row);
    }
    const value = this.extractValue(row, column.fieldName);
    return value;
  }

  private extractValue(obj: any, path: string): any {
    if (!obj || !path) return null;

    if (path.includes(".")) {
      const parts = path.split(".");
      let result = obj;
      for (const part of parts) {
        result = result?.[part];
        if (result === undefined || result === null) return null;
      }
      return result;
    }

    return obj[path];
  }

  // ==================== SUMMARY ====================

  getSummaryValue(column: ColumnConfig): any {
    if (!this.data || this.data.length === 0) return null;
    if (!column.summary) return null;

    switch (column.summary) {
      case "sum":
        return this.data.reduce((sum, row) => {
          const val = this.extractValue(row, column.fieldName);
          return sum + (Number(val) || 0);
        }, 0);
      case "count":
        return this.data.length;
      case "avg":
        const total = this.data.reduce((sum, row) => {
          const val = this.extractValue(row, column.fieldName);
          return sum + (Number(val) || 0);
        }, 0);
        return total / this.data.length;
      case "custom":
        return column.summaryFn ? column.summaryFn(this.data) : null;
      default:
        return null;
    }
  }

  hasSummary(): boolean {
    return !!(
      this.config?.display?.showSummaryRow &&
      this.config?.columns?.some((col) => col.summary)
    );
  }

  // ==================== BADGE ====================

  getBadgeClass(row: any, column: ColumnConfig): string {
    if (!column.badge) return "";

    const badge = column.badge;

    if (badge.colorFn) {
      return badge.colorFn(row);
    }

    if (badge.colorField) {
      return row[badge.colorField] || "";
    }

    if (badge.colorMap) {
      const value = this.extractValue(row, badge.field);
      return badge.colorMap[value] || "";
    }

    return "";
  }

  // ==================== ACTIONS VISIBILITY ====================

  isActionVisible(action: TableAction, row: any): boolean {
    if (typeof action.display === "function") {
      return action.display(row);
    }
    return action.display !== false;
  }

  getVisibleActions(): TableAction[] {
    return (this.config.actions || []).filter(
      (action) => action.display !== false
    );
  }

  hasActions(): boolean {
    return !!(this.config?.actions && this.config.actions.length > 0);
  }

  // ==================== UTILITIES ====================

  getRowIndex(index: number): number {
    return (this.currentPage - 1) * this.pageSize + index + 1;
  }

  getRowClass(row: any): string {
    let classes = "";
    if (this.config.rowClass) {
      if (typeof this.config.rowClass === "function") {
        classes = this.config.rowClass(row);
      } else {
        classes = this.config.rowClass;
      }
    }

    if (this.config.display?.treeMode) {
      const level = this.getRowLevel(row);
      classes += ` tree-row tree-row-level-${level}`;
      if (row._expanded) {
        classes += " tree-row-expanded";
      }
    }

    return classes.trim();
  }

  trackByFn(index: number, item: any): any {
    if (this.config?.trackByFn) {
      return this.config.trackByFn(index, item);
    }
    return item?.id || index;
  }

  getColspan(): number {
    let count = this.getVisibleColumns().length;
    if (this.config.display?.enableSelect) count++;
    if (this.config.display?.enableNumbering) count++;
    if (this.hasActions()) count++;
    return count;
  }

  @HostListener("window:resize")
  onResize(): void {
    this.cdr.detectChanges();
  }
}
